if (!customElements.get("homepage-campaign-carousel")) {
  class HomepageCampaignCarousel extends HTMLElement {
    constructor() {
      super();
      this.isDragging = false;
      this.dragOffset = 0;
      this.rafId = null;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector("[data-carousel-track]");
      this.slides = Array.from(this.querySelectorAll("[data-carousel-slide]"));
      this.prevButton = this.querySelector("[data-carousel-prev]");
      this.nextButton = this.querySelector("[data-carousel-next]");
      this.scrollbar = this.querySelector("[data-carousel-scrollbar]");
      this.thumb = this.querySelector("[data-carousel-scrollbar-thumb]");

      if (!this.track || this.slides.length < 1) return;

      this.track.addEventListener("scroll", this.onScroll, { passive: true });
      window.addEventListener("resize", this.onResize);

      this.prevButton?.addEventListener("click", () => this.moveBy(-1));
      this.nextButton?.addEventListener("click", () => this.moveBy(1));

      if (this.scrollbar && this.thumb) {
        this.scrollbar.addEventListener("pointerdown", this.onPointerDown);
        this.scrollbar.addEventListener("pointermove", this.onPointerMove);
        this.scrollbar.addEventListener("pointerup", this.onPointerUp);
        this.scrollbar.addEventListener("pointercancel", this.onPointerUp);
        this.scrollbar.addEventListener("keydown", this.onKeyDown);
      }

      this.update();
    }

    disconnectedCallback() {
      this.track?.removeEventListener("scroll", this.onScroll);
      this.scrollbar?.removeEventListener("pointerdown", this.onPointerDown);
      this.scrollbar?.removeEventListener("pointermove", this.onPointerMove);
      this.scrollbar?.removeEventListener("pointerup", this.onPointerUp);
      this.scrollbar?.removeEventListener("pointercancel", this.onPointerUp);
      this.scrollbar?.removeEventListener("keydown", this.onKeyDown);
      window.removeEventListener("resize", this.onResize);

      if (this.rafId) window.cancelAnimationFrame(this.rafId);
    }

    onScroll() {
      if (this.rafId) return;

      this.rafId = window.requestAnimationFrame(() => {
        this.rafId = null;
        this.update();
      });
    }

    onResize() {
      this.update();
    }

    moveBy(direction) {
      const currentIndex = this.getCurrentIndex();
      const nextIndex = Math.max(
        0,
        Math.min(this.slides.length - 1, currentIndex + direction),
      );
      this.goTo(nextIndex);
    }

    goTo(index) {
      const slide = this.slides[index];
      if (!slide) return;

      this.track.scrollTo({
        left: slide.offsetLeft - this.track.offsetLeft,
        behavior: "smooth",
      });
    }

    onPointerDown(event) {
      if (!this.scrollbar || !this.thumb) return;

      event.preventDefault();

      this.isDragging = true;
      this.track.classList.add("is-scrollbar-dragging");
      this.scrollbar.setPointerCapture?.(event.pointerId);

      const thumbRect = this.thumb.getBoundingClientRect();
      this.dragOffset =
        event.target === this.thumb
          ? event.clientX - thumbRect.left
          : thumbRect.width / 2;

      this.updateScrollFromPointer(event.clientX);
    }

    onPointerMove(event) {
      if (!this.isDragging) return;

      event.preventDefault();
      this.updateScrollFromPointer(event.clientX);
    }

    onPointerUp(event) {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.track.classList.remove("is-scrollbar-dragging");
      this.scrollbar?.releasePointerCapture?.(event.pointerId);
    }

    onKeyDown(event) {
      const maxScrollLeft = this.getMaxScrollLeft();
      if (maxScrollLeft <= 0) return;

      const step = this.getScrollStep();
      const keyActions = {
        ArrowLeft: () =>
          this.track.scrollBy({ left: -step, behavior: "smooth" }),
        ArrowRight: () =>
          this.track.scrollBy({ left: step, behavior: "smooth" }),
        PageUp: () =>
          this.track.scrollBy({
            left: -this.track.clientWidth,
            behavior: "smooth",
          }),
        PageDown: () =>
          this.track.scrollBy({
            left: this.track.clientWidth,
            behavior: "smooth",
          }),
        Home: () => this.scrollTo(0),
        End: () => this.scrollTo(maxScrollLeft),
      };

      const action = keyActions[event.key];
      if (!action) return;

      event.preventDefault();
      action();
    }

    updateScrollFromPointer(clientX) {
      if (!this.scrollbar || !this.thumb) return;

      const railRect = this.scrollbar.getBoundingClientRect();
      const maxThumbLeft = Math.max(0, railRect.width - this.thumb.offsetWidth);
      const thumbLeft = Math.min(
        Math.max(clientX - railRect.left - this.dragOffset, 0),
        maxThumbLeft,
      );
      const ratio = maxThumbLeft > 0 ? thumbLeft / maxThumbLeft : 0;

      this.scrollTo(this.getMaxScrollLeft() * ratio, "auto");
    }

    scrollTo(left, behavior = "smooth") {
      this.track.scrollTo({ left, behavior });
    }

    getMaxScrollLeft() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    getScrollStep() {
      const firstSlide = this.slides[0];
      if (!firstSlide) return this.track.clientWidth / 2;

      const styles = window.getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap || "0");

      return firstSlide.getBoundingClientRect().width + gap;
    }

    getCurrentIndex() {
      const scrollLeft = this.track.scrollLeft;
      const trackLeft = this.track.offsetLeft;
      let currentIndex = 0;
      let currentDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - trackLeft - scrollLeft);
        if (distance < currentDistance) {
          currentDistance = distance;
          currentIndex = index;
        }
      });

      return currentIndex;
    }

    update() {
      const maxScrollLeft = this.getMaxScrollLeft();
      const isScrollable = maxScrollLeft > 1;

      this.classList.toggle("is-not-scrollable", !isScrollable);

      if (this.prevButton) {
        this.prevButton.toggleAttribute(
          "disabled",
          !isScrollable || this.track.scrollLeft <= 1,
        );
      }

      if (this.nextButton) {
        this.nextButton.toggleAttribute(
          "disabled",
          !isScrollable || this.track.scrollLeft >= maxScrollLeft - 1,
        );
      }

      this.updateScrollbar(isScrollable);
    }

    updateScrollbar(isScrollable) {
      if (!this.scrollbar || !this.thumb) return;

      const maxScrollLeft = this.getMaxScrollLeft();
      this.scrollbar.hidden = !isScrollable;
      this.scrollbar.toggleAttribute("aria-hidden", !isScrollable);
      this.scrollbar.tabIndex = isScrollable ? 0 : -1;

      if (!isScrollable) return;

      const ratio =
        maxScrollLeft > 0 ? this.track.scrollLeft / maxScrollLeft : 0;
      const thumbWidthRatio =
        this.track.scrollWidth > 0
          ? this.track.clientWidth / this.track.scrollWidth
          : 1;
      const thumbWidth = Math.max(
        this.scrollbar.clientWidth * thumbWidthRatio,
        48,
      );
      const maxThumbLeft = Math.max(0, this.scrollbar.clientWidth - thumbWidth);

      this.thumb.style.width = `${thumbWidth}px`;
      this.scrollbar.style.setProperty(
        "--homepage-campaign-scrollbar-offset",
        `${maxThumbLeft * ratio}px`,
      );
      this.scrollbar.setAttribute("aria-valuenow", Math.round(ratio * 100));
    }
  }

  customElements.define("homepage-campaign-carousel", HomepageCampaignCarousel);
}
